// The offer agent in a customer thread (spec 09 §4.11 and spec 17 §4.1 as
// D-515 ruling 4 amends them; spec 14 §3.8.2): the run's state on the newest
// customer message, the agent's draft chip linking its journal card, and
// Draft offer.
//
// One strip, in the thread, serves both doors D-515 ruling 4 names. The claim
// panel cannot offer a draft before the thread has a customer, because the
// trigger is answered under the caller's reach of that customer (D-519 ruling
// 5) and an unclaimed thread has none, so Draft offer appears in this strip
// the moment the claim lands, beside the panel that made it.
//
// Who sees it: a reader whose `GET /v1/agents` answers the offer agent (an
// `agents.invoke` holder of the sales domain, or a manager of it). Everyone
// else sees nothing, not a disabled control (the copy budget's rule). The run
// and its action ids are read under the same reach, so a run on a customer the
// reader cannot see reads as no run.

import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { useOfferAgent } from "../../agents/components/draft-offer";
import {
  agentsKeys,
  runInFlight,
  triggerOnMessage,
  useMessageRun,
  useRun,
} from "../../agents/hooks/agents-queries";
import type { AgentListItem } from "../../agents/lib/agents-lib";
import { agentName, errorText, outcomeText } from "../../agents/lib/labels";
import type { Conversation, ThreadMessage } from "../hooks/messaging-queries";

type RunStatus = NonNullable<ReturnType<typeof useMessageRun>["data"]>["status"];

const RUN_VARIANT: Record<RunStatus, ChipVariant> = {
  queued: "neutral",
  running: "accent",
  awaiting_approval: "warn",
  completed: "ok",
  failed: "bad",
  cancelled: "neutral",
};

/**
 * The message Draft offer acts on: the newest inbound message from the
 * customer that is not a bot command. Intake fires on the same messages
 * (spec 17 §6, `messaging.inbound_intake_created`), so the strip shows the
 * run intake started on it, if any.
 */
export function draftTargetMessage(messages: readonly ThreadMessage[]): ThreadMessage | undefined {
  let newest: ThreadMessage | undefined;
  for (const message of messages) {
    if (message.direction !== "inbound" || message.sender_kind !== "contact") continue;
    if (message.body?.trimStart().startsWith("/") === true) continue;
    if (newest === undefined || Date.parse(message.created_at) > Date.parse(newest.created_at)) {
      newest = message;
    }
  }
  return newest;
}

/** The journal card the chip opens: the run's newest action, the one awaiting a person. */
export function draftActionId(actionIds: readonly string[] | undefined): string | undefined {
  return actionIds === undefined || actionIds.length === 0
    ? undefined
    : actionIds[actionIds.length - 1];
}

export function OfferAgentStrip({
  head,
  messages,
}: {
  head: Conversation;
  messages: readonly ThreadMessage[];
}) {
  const offer = useOfferAgent();
  if (head.kind !== "customer" || offer.agent === undefined) return null;
  const target = draftTargetMessage(messages);
  if (target === undefined) return null;
  return (
    <StripBody
      agent={offer.agent}
      canDraft={offer.canDraft && head.customer_id !== null}
      messageId={target.id}
    />
  );
}

function StripBody({
  agent,
  canDraft,
  messageId,
}: {
  agent: AgentListItem;
  canDraft: boolean;
  messageId: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const run = useMessageRun(agent.id, messageId);
  const current = run.data ?? null;
  const inFlight = current !== null && runInFlight(current.status);
  // The action ids exist once the run has stopped working; reading the detail
  // before then would cache an empty list for the staleness window.
  const detail = useRun(current?.id ?? null, current !== null && !inFlight);
  const actionId = draftActionId(detail.data?.action_ids);
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const draft = async () => {
    setStarting(true);
    setFailure(null);
    try {
      await triggerOnMessage(agent.id, messageId);
      await queryClient.invalidateQueries({
        queryKey: agentsKeys.messageRuns(agent.id, messageId),
      });
    } catch (error) {
      setFailure(error);
    } finally {
      setStarting(false);
    }
  };

  const outcome = current === null ? undefined : outcomeText(t, current.outcome);

  return (
    <section
      className="msg-agent-strip"
      aria-label={t("messaging.agent.stripAria")}
      data-testid="msg-agent-strip"
      data-run-status={current?.status ?? "none"}
    >
      <span className="msg-agent-name">{agentName(t, agent)}</span>
      {run.isPending ? (
        <span className="msg-hint" data-testid="msg-agent-loading">
          {t("messaging.agent.loading")}
        </span>
      ) : run.isError ? (
        <span className="msg-hint" role="alert" data-testid="msg-agent-error">
          {t("messaging.agent.error")}{" "}
          <Button
            size="sm"
            data-testid="msg-agent-retry"
            onClick={() => {
              void run.refetch();
            }}
          >
            {t("messaging.common.retry")}
          </Button>
        </span>
      ) : current !== null ? (
        <>
          <StatusChip variant={RUN_VARIANT[current.status]} data-testid="msg-agent-run">
            {t(`agents.runStatus.${current.status}`)}
          </StatusChip>
          {outcome !== undefined ? (
            <span className="msg-hint" data-testid="msg-agent-outcome">
              {outcome}
            </span>
          ) : null}
          {actionId !== undefined ? (
            <Link
              to="/agents/journal"
              search={{ action: actionId }}
              className="msg-agent-draft"
              data-testid="msg-agent-draft"
            >
              {t("messaging.agent.draftChip")}
            </Link>
          ) : null}
        </>
      ) : null}
      {canDraft && !inFlight && !run.isPending ? (
        <Button
          size="sm"
          className="msg-agent-offer"
          data-testid="msg-draft-offer"
          busy={starting}
          busyLabel={t("messaging.agent.starting")}
          onClick={() => {
            void draft();
          }}
        >
          {t("messaging.agent.draftOffer")}
        </Button>
      ) : null}
      {failure !== null ? (
        <p className="msg-error-note" role="alert" data-testid="msg-draft-offer-failed">
          {errorText(t, failure)}
        </p>
      ) : null}
    </section>
  );
}
